// Алиасы id уроков и блоков: смена источника чтения (черновик ⇄ живой курс)
// меняет id тех же сущностей, а цели, снятые раньше (задача загрузки, список
// блоков прогона), держат старые. Алиас «старый id → новый» переводит их на ту
// же сущность в новом источнике.

export type AliasKind = 'lesson' | 'block'
export type IdAliases = Record<string, number>
export type IdPair = [number, number | null]

const aliasKey = (kind: AliasKind, id: number) => `${kind}:${id}`

// Страховка от цикла: правило записи (`writeAliases`) цикла не допускает, но
// разматывание не должно зависнуть и на испорченном состоянии.
const MAX_ALIAS_STEPS = 8

/** Текущий id сущности по цепочке алиасов (до неподвижной точки). */
export const resolveAlias = (aliases: IdAliases, kind: AliasKind, id: number): number => {
  let current = id
  for (let step = 0; step < MAX_ALIAS_STEPS; step++) {
    const next = aliases[aliasKey(kind, current)]
    if (next === undefined || next === current) return current
    current = next
  }
  return current
}

/**
 * Пишет пары в алиасы. Сначала снимается алиас, ключом которого стоит НОВЫЙ id:
 * сущность с этим id снова текущая. Без этого слияние черновика (51 → 11) после
 * его создания (11 → 51) оставило бы 11 → 51 и 51 → 11 — цикл, а живой id 11
 * разворачивался бы в id уже удалённого черновика.
 */
export const writeAliases = (aliases: IdAliases, kind: AliasKind, pairs: IdPair[]) => {
  for (const [from, to] of pairs) {
    if (to === null) continue
    delete aliases[aliasKey(kind, to)]
    if (from !== to) aliases[aliasKey(kind, from)] = to
  }
}

/** id по парам: пара нашлась — её новый id (`null` — сущности больше нет). */
export const mapByPairs = (id: number, pairs: IdPair[]): number | null => {
  const pair = pairs.find(([from]) => from === id)
  return pair ? pair[1] : id
}

/** Часть состояния редактора, которую переводит смена источника чтения. */
export interface RemappedSelection {
  selectedLessonId: number | null
  selectedBlockId: number | null
  selectedBlockIds: number[]
  idAliases: IdAliases
}

/**
 * Смена источника чтения (появился черновик, его слили или отменили): те же
 * сущности приехали с другими id. Выделение переводится по парам `[старый,
 * новый]` (`null` — сущности больше нет: выделение снимается), пары с новым
 * id записываются в алиасы для целей, снятых раньше (загрузки, прогоны).
 * Мутирует черновик immer — вызывается из редьюсера `remapSelection`.
 */
export const applyRemap = (
  state: RemappedSelection,
  { lessons, blocks }: { lessons: IdPair[]; blocks: IdPair[] },
): void => {
  if (state.selectedLessonId !== null)
    state.selectedLessonId = mapByPairs(state.selectedLessonId, lessons)
  if (state.selectedBlockId !== null)
    state.selectedBlockId = mapByPairs(state.selectedBlockId, blocks)
  const ids = state.selectedBlockIds.map((id) => mapByPairs(id, blocks))
  state.selectedBlockIds = [...new Set(ids.filter((id): id is number => id !== null))]
  // Урока больше нет — его блоков тоже, даже если пар для них не прислали.
  if (state.selectedLessonId === null) {
    state.selectedBlockId = null
    state.selectedBlockIds = []
  }
  writeAliases(state.idAliases, 'lesson', lessons)
  writeAliases(state.idAliases, 'block', blocks)
}
